import moment from "moment";
import React, { Component } from "react";
import { Chart } from "react-google-charts";
import Loading from "./Loading";
import i18n from "../i18n";

const DURATION_LIST = [
  { key: "1W", days: 7 },
  { key: "2W", days: 14 },
  { key: "1M", days: 30 },
  { key: "3M", days: 90 },
  { key: "6M", days: 180 },
  { key: "1Y", days: 365 },
  { key: "2Y", days: 730 },
  { key: "5Y", days: 1825 },
];

const XTICK_CONFIG = {
  "1W":  { points: 5, intervalDays: 2, format: "dd\nMMM" },
  "2W":  { points: 6, intervalDays: 3, format: "dd\nMMM" },
  "1M":  { points: 5, intervalDays: 8, format: "dd\nMMM" },
  "3M":  { points: 7, intervalDays: 16, format: "dd\nMMM" },
  "6M":  { points: 7, intervalDays: 31, format: "MMM\nYYYY" },
  "1Y":  { points: 5, intervalDays: 92, format: "MMM\nYYYY" },
  "2Y":  { points: 5, intervalDays: 183, format: "MMM\nYYYY" },
  "5Y":  { points: 6, intervalDays: 366, format: "MMM\nYYYY" },
};
class GraphChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      trendS: 2,
      trendF: 2,
      chartState: "",
    };
  }

  // Chin's logic for y-axis scaling (following spec)
  calculateYAxisTicks(dataArray) {
    const multiple = 2;

    if (!Array.isArray(dataArray) || dataArray.length === 0) {
      return [0, 1, 2, 3, 4, 5]; // default fallback
    }

    // Convert to float and filter invalid
    const values = dataArray.map(d => parseFloat(d.y)).filter(v => !isNaN(v));
    let severityMin = Math.min(...values);
    let severityMax = Math.max(...values) + 1.0;

    let start, end;

    if (severityMin === 0) {
      start = 0;
    } else {
      start = Math.floor((severityMin - 1.0) / multiple) * multiple;
    }

    end = Math.ceil(severityMax / multiple) * multiple;

    const interval = (end - start) / 4;

    const ticks = [];
    for (let i = 0; i <=4; i++) {
      ticks.push(parseFloat((start + interval * i).toFixed(2)));
    }
    ticks.push(parseFloat(end.toFixed(2)));

    return ticks;
  }

  render() {
    const {
      firstData,
      secondData,
      xLabelsData,
      optionForm,
    } = this.props;

    // Count non-null numeric points for each series
    const countValid = (arr) =>
      Array.isArray(arr)
        ? arr.reduce((n, d) => (d && d.y != null && !isNaN(Number(d.y)) ? n + 1 : n), 0)
        : 0;

    // Count number of datapoints
    const sCount = countValid(firstData);   // symptoms
    const fCount = countValid(secondData);  // factors

    // ===== Dynamic Tick Calculation for Y-Axis (Left and Right) =====
    const minmax_Symptom = this.calculateYAxisTicks(firstData);
    const minmax_Factor = this.calculateYAxisTicks(secondData);
    const symptomTicks = minmax_Symptom.length > 0 ? minmax_Symptom : [];
    const factorTicks = minmax_Factor.length > 0 ? minmax_Factor : [];

    // ===== Horizontal Axis Format Selection =====
    const timeSelection = optionForm.end;
    let scaleKey = timeSelection;
    const userDuration = DURATION_LIST.find((b) => b.key === timeSelection);
    let hAxisFormat = userDuration ? userDuration.format : "dd\nMMM";

    const tickConfig = XTICK_CONFIG[scaleKey];
    const tickStartDate = moment(optionForm.start).startOf("day");

    let xLabels = [];

    for (let i = 0; i < tickConfig.points; i++) {
      const tickDate = tickStartDate.clone().add(i * tickConfig.intervalDays, "days");
      xLabels.push(
        new Date(Date.UTC(tickDate.year(), tickDate.month(), tickDate.date()))
      );
    }

    // Trendline to be generated when there's data and the number of datapoints are more than 1
    const trendlines = {};
    if (this.state.trendS !== 0 && sCount >= 2) {
      trendlines[0] = {
        tooltip: false,
        type: "polynomial",
        degree: this.state.trendS,
        visibleInLegend: false,
        color: "rgb(255, 0, 0)",
        lineWidth: 3,
        opacity: 1,
      };
    }
    if (this.state.trendF !== 0 && fCount >= 2) {
      trendlines[1] = {
        tooltip: false,
        type: "polynomial",
        degree: this.state.trendF,
        visibleInLegend: false,
        color: "rgb(0, 123, 255)",
        lineWidth: 3,
        opacity: 1,
      };
    }

    const options = {
      title: null,
      series: {
        0: {
          targetAxisIndex: 0,
          pointShape: "square",
          color: "rgb(255, 0, 0)",
          pointSize: 9,
          pointHoverRadius: 15,
          pointRadius: 10,
          zIndex: 50,
          tooltip: false,
        },
        1: {
          targetAxisIndex: 1,
          pointShape: { type: "circle" },
          color: "rgb(0, 123, 255)",
          pointSize: 5,
          pointHoverRadius: 15,
          pointRadius: 10,
          zIndex: 50,
          tooltip: {
            isHtml: true,
          },
        },
      },
      vAxes: [
        firstData.length > 0
        ? {
            type: "number",
            ticks: symptomTicks,
            gridlines: { color: "#454545" },
            minorGridlines: { color: "transparent" },
            viewWindow:
            {
              min: Math.min(...symptomTicks),
              max: Math.max(...symptomTicks),
            },
            textStyle: {
              color: "#bbb",
              fontSize: 10,
            },
            format: "#.##",
            baseline: Math.min(...symptomTicks) - 1000, // move Google default baseline far beyond graph area
            baselineColor: "transparent" // change the color of Google default baseline to hide it visually
          }
        : {
            textStyle: {
              color: "transparent",
              fontSize: 10,
            },
            gridlines: { color: "transparent" },
            minorGridlines: { color: "transparent" },
            baselineColor: "transparent",
          },


        secondData.length > 0
        ? {
            type: "number",
            ticks: factorTicks,
            gridlines: { color: "#454545" },
            minorGridlines: { color: "transparent" },
            viewWindow:
            {
              min: Math.min(...factorTicks),
              max: Math.max(...factorTicks),
            },
            textStyle: {
              color: "#bbb",
              fontSize: 10,
            },
            format: "#.##",
            baseline: Math.min(...factorTicks) - 1000, // move Google default baseline far beyond graph area
            baselineColor: "transparent" // change the color of Google default baseline to hide it visually
          }
        : {
            textStyle: {
              color: "transparent",
              fontSize: 10,
            },
            gridlines: { color: "transparent" },
            minorGridlines: { color: "transparent" },
            baselineColor: "transparent",
          },
      ],
      hAxis: {
        type: "date",
        format:tickConfig.format,
        ticks: xLabels,
        showTextEvery: 1,
        viewWindow: {
          min: xLabels[0],
          max: xLabels[xLabels.length - 1],
        },
        gridlines: { color: "transparent" },
        minorGridlines: { color: "transparent" },
        textStyle: {
          color: "#bbb",
          fontSize: 10,
          fontStyle: "bold",
        },
        baselineColor: "transparent",
      },

      legend: "none",
      crosshair: {
        trigger: "none",
        orientation: "both",
        opacity: 0,
      },
      backgroundColor: "transparent",
      chartArea: {
        left: 40,
        top: 15,
        right: 40,
        bottom: 40,
        backgroundColor: {
          fill: "transparent",
          stroke: "transparent",
          opacity: 1,
        },
      },
      curveType: "function",
      enableInteractivity: true,
      colors: ["rgb(255, 0, 0)", "rgb(0, 123, 255)"],
      trendlines,
      // trendlines: {
      //   0: {
      //     tooltip: false,
      //     type: "polynomial",
      //     degree: this.state.trendS,
      //     visibleInLegend: false,
      //     color: "rgb(255, 0, 0)",
      //     lineWidth: 3,
      //     opacity: this.state.trendS === 0 ? 0 : 1,
      //   },
      //   1: {
      //     tooltip: false,
      //     type: "polynomial",
      //     degree: this.state.trendF,
      //     visibleInLegend: false,
      //     color: "rgb(0, 123, 255)",
      //     lineWidth: 3,
      //     opacity: this.state.trendF === 0 ? 0 : 1,
      //   },
      // }, // Draw a trendline for data series 0.
      tooltip: {
        isHtml: true,
      },
    };
    let symptomData = [
      [
        "Date",
        "Severity",
        "Factor",
        { role: "tooltip", type: "string", p: { html: true } },
        { type: "string", role: "style" },
      ],
    ];
    firstData.map((item, index) => {
      symptomData.push([
        new Date(item.x),
        Number(item.y),
        NaN,
        false,
        "point { shape-type: square;}",
      ]);
    });
    secondData.map((item, index) => {
      symptomData.push([
        new Date(item.x),
        NaN,
        Number(item.y),
        item.items && item.items !== undefined
          ? `<div style="border:1px solid #333;background-color:#222;text-align:left;font-size:0.6rem;color:#999;border-radius:5px;padding:5px">${item.items.map((item) => item.fullname.toString() + "<br>").join(" ")}</div>`
          : `<div style="border:1px solid #333;background-color:#222;text-align:left;font-size:0.6rem;color:#999;border-radius:5px;padding:5px">${item.fullname && item.fullname.toString()}</div>`,
        "point { size: 2.5; shape-type: circle; fill-color: transparent; stroke-color: rgb(0, 123, 255); stroke-width: 2}",
      ]);
    });

    return (
      <div
        style={{
          position: "absolute",
          top: 20,
          left: 3,
          right: 0,
          bottom: 22,
        }}
      >
        <div style={{ width: "100%", height: 40 }}>
          <span>
            {/* Left (Symptom Axis) header + toggle */}
            <span
              style={{
                color: "#ff0000",
                float: "left",
                display: "inline-block",
                marginTop: "-20px"
              }}
            >
              {this.props.t("general.CATEGORY_SYMPTOM")}
              {"  "}
              {
                sCount >= 2 ? (
                  <span
                    onClick={() => {
                      if (this.state.trendS < 4) {
                        this.setState({ trendS: this.state.trendS + 1 });
                      } else {
                        this.setState({ trendS: 0 });
                      }
                    }}
                    style={{
                      backgroundColor: "#ff0000",
                      color: "#fff",
                      fontSize: 12,
                      display: "inline-block",
                      marginRight: 5,
                      padding: "0px 0px 2px 0px",
                      cursor: "pointer",
                      borderRadius: 4,
                      width: "6rem",
                      textAlign: "center",
                    }}
                  >
                    {this.state.trendS === 0
                      ? i18n.t("general.NO_LINE_BUTTON_TEXT")
                      : this.state.trendS === 1
                        ? i18n.t("general.LINEAR_BUTTON_TEXT")
                        : i18n.t("general.POLYNOMINAL_BUTTON_TEXT") +
                        this.state.trendS}
                  </span>
                ) : sCount === 1 ? (
                  <span
                    style={{
                      backgroundColor: "#ff0000",
                      color: "#fff",
                      fontSize: 12,
                      display: "inline-block",
                      marginRight: 5,
                      padding: "0px 0px 2px 0px",
                      borderRadius: 4,
                      width: "6rem",
                      textAlign: "center",
                    }}
                  >
                    {this.props.t("graph.ONLY_1_POINT")}
                  </span>
                ) : null
              }
              <br></br>
              <span style={{ color: "white" }}>
                {firstData.length === 0 && this.props.t("graph.NO_DATA_TEXT")}
              </span>
              <span style={{ color: "red", fontSize: '0.7rem' }}>
                <span style={{ color: "red", fontSize: '0.7rem' }}>
                  <span style={{ color: "red", fontSize: '0.7rem' }}>

                    {firstData.length === 0 ? "" :
                      ((optionForm.symptom_unit !== undefined &&  optionForm.symptom_unit !=="") ?
                        `(${optionForm.symptom_unit})` : "(No Unit)"
                      )
                    }
                  </span>
                </span>
              </span>
            </span>

            {/* Right (Factor) header + toggle */}
            <span
              style={{
                color: "#007bff",
                float: "right",
                display: "inline-block",
                textAlign: "right",
                marginTop: "-20px"
              }}
            >
               {
                fCount >= 2 ? (
                  <span
                    onClick={() => {
                      if (this.state.trendF < 4) {
                        this.setState({ trendF: this.state.trendF + 1 });
                      } else {
                        this.setState({ trendF: 0 });
                      }
                    }}
                    style={{
                      backgroundColor: "#007bff",
                      color: "#fff",
                      fontSize: 12,
                      display: "inline-block",
                      padding: "0px 0px 2px 0px",
                      cursor: "pointer",
                      borderRadius: 4,
                      width: "6rem",
                      textAlign: "center",
                    }}
                  >
                    {this.state.trendF === 0
                      ? i18n.t("general.NO_LINE_BUTTON_TEXT")
                      : this.state.trendF === 1
                        ? i18n.t("general.LINEAR_BUTTON_TEXT")
                        : i18n.t("general.POLYNOMINAL_BUTTON_TEXT") +
                        this.state.trendF}
                  </span>
                ) : fCount === 1 ? (
                  <span
                    style={{
                      backgroundColor: "#007bff",
                      color: "#fff",
                      fontSize: 12,
                      display: "inline-block",
                      padding: "0px 0px 2px 0px",
                      borderRadius: 4,
                      width: "6rem",
                      textAlign: "center",
                    }}
                  >
                    {this.props.t("graph.ONLY_1_POINT")}
                  </span>
                ) : null
              }
              {"  "}
              {this.props.t("general.NUTRIENTS_FIELD_TEXT")}
              <br></br>
              <span style={{ color: "white" }}>
                {secondData.length === 0 && this.props.t("graph.NO_DATA_TEXT")}
              </span>
              <span style={{ color: "#007bff", fontSize: '0.7rem' }}>
                {
                  secondData.length !== 0 &&
                    secondData[0].unit &&
                    (secondData[0].unit !== 'Normal10' && secondData[0].unit !== 'N/A') ?
                    `(${secondData[0].unit})` : secondData.length !== 0 && optionForm.trigger_unit !== undefined ? `(${optionForm.trigger_unit})` : ""
                }
              </span>
            </span>
          </span>

          <div style={{ clear: "both" }}></div>
        </div>
        {this.state.chartState === "ready" && (
          <div
            style={{
              position: "absolute",
              top: 40,
              left: 40,
              bottom: 15,
              right: 41,
            }}
          >
            {xLabels &&
              xLabels.map((item, index) => {
                if (index < xLabels.length) {
                  return (
                    <div
                      key={index}
                      style={{
                        height: "100%",
                        borderLeft:
                          index === 0
                            ? "1px solid rgb(255, 0, 0)"
                            : index === xLabels.length - 1
                              ? "1px solid rgb(0, 123, 255)"
                              : "1px solid #454545",
                        position: "absolute",
                        bottom: 0,
                        left: (100 / (xLabels.length - 1)) * index + "%",
                        zIndex:
                          index === 0
                            ? 1
                            : index === xLabels.length - 1
                              ? 1
                              : undefined,
                      }}
                    />
                  );
                }
              })}
          </div>
        )}
        <Chart
          chartType="ScatterChart"
          data={symptomData}
          loader={<Loading isLoading={true} />}
          options={options}
          width="100%"
          height="100%"
          // legendToggle
          chartEvents={[
            {
              eventName: "ready",
              callback: () => {
                if (this.state.chartState !== "ready") {
                  this.setState({ chartState: "ready" });
                }
              },
            },
            {
              // Prevent the square outline on any point click (symptom/factor)
              eventName: "select",
              callback: ({ chartWrapper }) => {
                try {
                  const chart = chartWrapper.getChart();
                  if (chart && chart.setSelection) chart.setSelection([]);
                } catch (_) {
                  /* no-op */
                }
              },
            },
          ]}
          render={({ renderChart }) => {
            return (
              <div style={{ width: "100%", height: "100%", position: "relative", bottom: "15px" }}>
                {renderChart()}
              </div>
            );
          }}
        />
      </div>
    );
  }
}

export default GraphChart;



// WEBPACK FOOTER //
// ./src/components/GraphChart.js